/**
 * RegulationGauge — animated Apple-Watch-style progress ring.
 *
 * Renders a circular SVG ring that sweeps from 0 to `value` (0-100)
 * on mount. Ring color reflects the score band. The center shows the
 * number in a large tabular typography with a small label below.
 *
 * Reusable across:
 *   • Result screen of the assessment flow
 *   • Permanent tile in the dashboard
 *   • Future timeline / history view
 */
import { useEffect, useState } from "react";
import { bandForScore } from "@/data/regulation";

export default function RegulationGauge({
  value = 0,
  size = 200,
  thickness = 12,
  label = "Índice",
  sublabel,
  animate = true,
  className = "",
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const band = bandForScore(clamped);
  const [displayed, setDisplayed] = useState(animate ? 0 : clamped);

  // Sweep animation — both ring stroke and number tick in sync.
  useEffect(() => {
    if (!animate) {
      setDisplayed(clamped);
      return;
    }
    let raf;
    const start = performance.now();
    const duration = 1200;
    const from = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(Math.round(from + (clamped - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [clamped, animate]);

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (displayed / 100) * circumference;
  const gap = circumference - dash;

  return (
    <div
      data-testid="regulation-gauge"
      data-value={clamped}
      data-band={band.id}
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={thickness}
        />
        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={band.color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${gap}`}
          style={{
            filter: `drop-shadow(0 0 6px ${band.color}55)`,
            transition: "stroke 300ms ease",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="text-[10px] tracking-[0.32em] uppercase font-semibold"
          style={{ color: `${band.color}` }}
        >
          {label}
        </div>
        <div
          className="text-[52px] leading-none font-semibold tabular-nums mt-1"
          style={{ color: "#FFFFFF" }}
        >
          {displayed}
        </div>
        {sublabel && (
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 font-medium mt-2">
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}
